import { useGetAllPermissionsQuery } from "../../../hooks/queries/usePermissionQueries";
import { ConfirmModal, PermissionModal } from "../modals/RBACModals";
import FetchFeedback from "../../ui/CustomFetchFeedback/CustomFetchFeedback";
import PermissionFilters from "./components/permissions/PermissionFilters";
import PermissionTableContent from "./components/permissions/PermissionTableContent";
import { usePermissionFilters } from "../hooks/permissions/usePermissionFilters";
import { usePermissionModal } from "../hooks/permissions/usePermissionModal";
import { usePermissionForm } from "../hooks/permissions/usePermissionForm";
import { usePermissionHandlers } from "../hooks/permissions/usePermissionHandlers";

const PermissionsTabContent = ({ permissions, loadingData }) => {
  const filterHook = usePermissionFilters({ permissions });
  const formHook = usePermissionForm();
  const modalHook = usePermissionModal({ onModalClose: formHook.resetForm });

  const handlerHook = usePermissionHandlers({
    modalMode: modalHook.modalMode,
    selectedPermission: modalHook.selectedPermission,
    closeModal: modalHook.closeModal,
  });

  // Handle edit permission - prefill form when editing
  const handleEditPermission = (permission) => {
    formHook.prefillForm(permission);
    modalHook.openEditModal(permission);
  };

  // Prepare form submission handler
  const handleSubmit = formHook.handleSubmit(handlerHook.handleSubmit);

  return (
    <div className="flex flex-col min-h-full">
      <PermissionFilters
        onSearchChange={filterHook.setSearchTerm}
        badgeCount={filterHook.filteredPermissions.length}
        onAddPermission={modalHook.openCreateModal}
        isCreating={handlerHook.isCreating}
      />

      <div className="flex-1">
        <PermissionTableContent
          loadingData={loadingData}
          noPermissions={filterHook.noPermissions}
          noResults={filterHook.noResults}
          filteredPermissions={filterHook.filteredPermissions}
          onEdit={handleEditPermission}
          onDelete={handlerHook.handleDeletePermission}
        />
      </div>

      <PermissionModal
        opened={modalHook.modalOpen}
        onClose={modalHook.closeModal}
        onSubmit={handleSubmit}
        control={formHook.control}
        isSubmitting={handlerHook.isCreating || handlerHook.isUpdating}
        mode={modalHook.modalMode}
      />

      <ConfirmModal
        opened={handlerHook.confirmOpen}
        onConfirm={handlerHook.handleConfirmDelete}
        onCancel={handlerHook.handleCancelDelete}
        title="Delete Permission"
        message={
          handlerHook.pendingDelete
            ? `Are you sure you want to delete '${handlerHook.pendingDelete.name}'?`
            : "Are you sure you want to delete this permission?"
        }
        isLoading={handlerHook.isDeleting}
        confirmLabel={handlerHook.isDeleting ? "Deleting..." : "Yes, delete"}
        cancelLabel="Cancel"
      />
    </div>
  );
};

const PermissionsTab = () => {
  const {
    data: permissions = [],
    isLoading,
    isError,
    refetch,
  } = useGetAllPermissionsQuery();

  if (isError) {
    return (
      <FetchFeedback
        message="Failed to load permissions. Please try again."
        onRetry={refetch}
      />
    );
  }

  return (
    <PermissionsTabContent permissions={permissions} loadingData={isLoading} />
  );
};

export default PermissionsTab;
